@file:Suppress("UnsafeCastFromDynamic")

import ai.hypergraph.kaliningraph.*
import ai.hypergraph.kaliningraph.parsing.*
import ai.hypergraph.kaliningraph.repair.*
import ai.hypergraph.tidyparse.*
import ai.hypergraph.tidyparse.wgpu.*
import kotlinx.browser.*
import kotlinx.coroutines.*
import org.w3c.dom.*
import org.w3c.dom.events.*
import kotlin.js.Promise
import kotlin.time.Duration.Companion.nanoseconds

val cnfInputField by lazy { document.getElementById("tidyparse-input") as HTMLTextAreaElement }
val cnfOutputField by lazy { document.getElementById("tidyparse-output") as Node }

// ---- Minimal CNF-only editor ----
open class JSTidyCNFEditor(editor: HTMLTextAreaElement, output: Node) : JSTidyEditor(editor, output) {
  /** Load a CNF from text and refresh highlighting */
  fun loadCNFFromText(text: String) {
    discardSoftTerminalCompletion()
    runningJob?.cancel()
    currentWorkHash = 0
    cfg = text.parseCNF()
  }

  override fun getLatestCFG(): CFG = cfg

  override fun caretIsInGrammar(): Boolean = false

  override suspend fun holeCompletionCandidates(cfg: CFG, tokens: List<Σᐩ>): Sequence<Σᐩ> =
    if (gpuAvailable) super.holeCompletionCandidates(cfg, tokens)
    else {
      var iterations = 0
      cfg.enumSeqSmartSuspendable(tokens) {
        if (iterations++ % 3 == 0) delay(50.nanoseconds)
      }
    }

  override fun redecorateLines(cfg: CFG) { decorator.fullDecorate(cfg) }
}

lateinit var jsCnfEditor: JSTidyCNFEditor

// ---- Modal + wiring for CNF ----
fun cnfSetup() {
  initTidyCodeMirror()
  jsCnfEditor = JSTidyCNFEditor(cnfInputField, cnfOutputField)
  initSplitLayout { jsCnfEditor.run { continuation { handleInput() } } }
  val overlay = buildCnfModal()
  document.body?.appendChild(overlay)

  // Wire keystrokes to the CNF editor (parse/complete/repair)
  var lastCaretStart = cnfInputField.selectionStart!!
  var lastCaretEnd = cnfInputField.selectionEnd!!
  cnfInputField.addEventListener("selectionchange", {
    val start = cnfInputField.selectionStart!!
    val end = cnfInputField.selectionEnd!!
    if (start == end && (start != lastCaretStart || end != lastCaretEnd))
      jsCnfEditor.run { continuation { handleInput() } }
    lastCaretStart = start
    lastCaretEnd = end
  })
  cnfInputField.addEventListener("input", { jsCnfEditor.run { continuation { handleInput() } } })
  cnfInputField.addEventListener("input", { jsCnfEditor.redecorateLines() })
  val cm = window.asDynamic().cmEditor
  if (cm != null && cm != js("undefined")) cm.on("keydown") { _: dynamic, e: dynamic -> jsCnfEditor.navUpdate(e) }
  else cnfInputField.addEventListener("keydown", { e -> jsCnfEditor.navUpdate(e as KeyboardEvent) })

// Optional: read initial settings and keep LED buffer in sync
  val ledBuffSel = document.getElementById("led-buffer") as? HTMLInputElement
  LED_BUFFER = ledBuffSel?.value?.toIntOrNull() ?: LED_BUFFER
  ledBuffSel?.addEventListener("change", { LED_BUFFER = ledBuffSel.value.toInt() })

  fun show() {
    overlay.style.display = "flex"
    document.documentElement?.classList?.add("cnf-lock-scroll")
    window.setTimeout({
      (document.getElementById("cnfFileInput") as? HTMLInputElement)?.focus()
    }, 50)
  }
  fun hide() {
    overlay.style.display = "none"
    document.documentElement?.classList?.remove("cnf-lock-scroll")
  }

  // Open on DOM ready; if we're already past loading, show immediately.
  document.addEventListener("DOMContentLoaded", { show() })
  if (document.readyState != DocumentReadyState.LOADING) show()

  var lastUrl: String? = null
  val input = document.getElementById("cnfFileInput") as HTMLInputElement
  val fileName = document.getElementById("cnfFileName") as HTMLSpanElement

  fun installCnfTextSource(name: String, text: String, size: Number = text.length) {
    fileName.textContent = name

    val obj = js("{}")
    obj.name = name
    obj.size = size
    obj.type = "text/plain"
    obj.url = null
    obj.text = text
    obj.bytes = null
    window.asDynamic().tidySelectedFile = obj

    val init = js("{}")
    init.detail = obj
    window.dispatchEvent(js("new CustomEvent('tidy:cnf-loaded', init)") as Event)

    jsCnfEditor.loadCNFFromText(text)
  }

  fun finishCnfLoad(name: String, text: String) {
    jsCnfEditor.run { continuation { handleInput() } }
    jsCnfEditor.redecorateLines()

    log("Loaded ${text.length} bytes from $name")
    hide()
    if (cm != null && cm != js("undefined")) cm.focus() else cnfInputField.focus()
  }

  fun loadCnfTextFromSource(name: String, text: String, size: Number = text.length) = try {
    installCnfTextSource(name, text, size)
    finishCnfLoad(name, text)
  } catch (e: dynamic) { console.error("Failed to parse CNF:", e) }

  val gpuReady = MainScope().launch { tryBootstrappingGPU(true) }
  (document.getElementById("pythonCnfBtn") as HTMLButtonElement)
    .addEventListener("click", { MainScope().launch { try {
        val text = pythonStatementCNFAllProds.joinToString("\n") { it.pretty() }
        installCnfTextSource("Python CNF", text)
        val cfg = jsCnfEditor.getLatestCFG()
        gpuReady.join()
        loadPythonPCFG(cfg)
        if (jsCnfEditor.getLatestCFG() === cfg) finishCnfLoad("Python CNF", text)
        if (gpuAvailable) loadWDFA("wdfa.bin")
      } catch (e: dynamic) { console.error("Python CNF / WDFA load error:", e) }
    }})

  input.addEventListener("change", { _ ->
    MainScope().launch {
      try {
        val f = input.files?.item(0) ?: return@launch
        fileName.textContent = f.name

        // Update/revoke blob URL
        lastUrl?.let { js("URL.revokeObjectURL")(it) }
        val url = js("URL.createObjectURL")(f) as String
        lastUrl = url

        // Read text (+ bytes optional)
        val text = try { f.asDynamic().text().unsafeCast<Promise<String>>().await() } catch (_: Throwable) { null }
        val ab = try { f.asDynamic().arrayBuffer().unsafeCast<Promise<org.khronos.webgl.ArrayBuffer>>().await() } catch (_: Throwable) { null }
        val bytes = ab?.let { org.khronos.webgl.Uint8Array(it) }

        // Publish to window
        val obj = js("{}")
        obj.file = f; obj.name = f.name; obj.size = f.size
        obj.type = f.type; obj.url = url
        obj.text = text; obj.bytes = bytes
        window.asDynamic().tidySelectedFile = obj

        // Notify listeners
        val init = js("{}"); init.detail = obj
        window.dispatchEvent(js("new CustomEvent('tidy:cnf-loaded', init)") as Event)

        // Parse + decorate (if you do this here)
        text?.let { loadCnfTextFromSource(f.name, it, f.size) }
      } catch (e: dynamic) { console.error("CNF load error:", e) }
    }
  })

  // Optional: only allow Esc to close after something was chosen
  document.addEventListener("keydown", { e ->
    if ((e as KeyboardEvent).key == "Escape" && window.asDynamic().tidySelectedFile != undefined) hide()
  })

}

private fun buildCnfModal(): HTMLDivElement {
  val overlay = (document.createElement("div") as HTMLDivElement).apply {
    id = "cnfModal"; className = "cnf-modal__overlay"
    setAttribute("role","dialog"); setAttribute("aria-modal","true")
    setAttribute("aria-labelledby","cnfModalTitle")
  }
  val dialog = (document.createElement("div") as HTMLDivElement).apply { className = "cnf-modal__dialog" }
  val header = (document.createElement("div") as HTMLDivElement).apply {
    className = "cnf-modal__header"; id = "cnfModalTitle"; textContent = "Load a context-free grammar (CFG)"
  }

  val body = (document.createElement("div") as HTMLDivElement).apply {
    className = "cnf-modal__body"
    innerHTML = """
      <p>CFG requirements:</p>
      <ul>
        <li>The start symbol must be named <code>START</code></li>
        <li>Grammar must be in <a style="color:white;" href="https://en.wikipedia.org/wiki/Chomsky_normal_form">CNF</a>, i.e., all productions are either:
          <ul>
            <li>Binary: <code>W -&gt; X Z</code> (with <code>W, X, Z ∈ V</code> nonterminals), or</li>
            <li>Unary (lexical): <code>W -&gt; a</code> (with <code>a ∈ Σ</code> terminal)</li>
          </ul>
        </li>
        <li>One production per line, no alternation (<code>|</code>) allowed</li>
        <li>All tokens are space-separated, no spaces in terminals</li>
        <li>Custom CFG file has a `.cnf` suffix</li>
      </ul>
      <p>Example:<br><br>
        <code>START -&gt; EXPR EXPR</code><br>
        <code>EXPR -&gt; LPAREN EXPR</code><br>
        <code>LPAREN -&gt; (</code>
      </p>
    """.trimIndent()
  }

  val footer = (document.createElement("div") as HTMLDivElement).apply { className = "cnf-modal__footer" }
  val leftFooter = (document.createElement("div") as HTMLDivElement).apply { className = "cnf-modal__footer-left" }

  val btn = (document.createElement("label") as HTMLLabelElement).apply {
    className = "cnf-btn"
    setAttribute("for","cnfFileInput")
    title = "Choose a CNF file"
    textContent = "Custom CNF"
  }

  val input = (document.createElement("input") as HTMLInputElement).apply {
    id = "cnfFileInput"
    className = "cnf-input"
    type = "file"
    accept = ".cnf"
  }

  val fileName = (document.createElement("span") as HTMLSpanElement).apply {
    id = "cnfFileName"
    className = "cnf-file-name"
    setAttribute("aria-live","polite")
    textContent = "No file selected"
  }

  val pythonCnfBtn = (document.createElement("button") as HTMLButtonElement).apply {
    id = "pythonCnfBtn"
    className = "cnf-btn cnf-btn--python"
    type = "button"
    title = "Load the built-in Python CNF grammar"
    textContent = "Python CNF"
  }

  btn.appendChild(input)

  leftFooter.appendChild(btn)
  leftFooter.appendChild(fileName)

  footer.appendChild(leftFooter)
  footer.appendChild(pythonCnfBtn)

  dialog.appendChild(header)
  dialog.appendChild(body)
  dialog.appendChild(footer)
  overlay.appendChild(dialog)

  return overlay
}
